import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ContentItem } from '@/hooks/signage';
import { DashboardRenderer } from './renderers/DashboardRenderer';

interface ContentRendererProps {
  content: ContentItem;
  onComplete?: () => void;
}

export function ContentRenderer({ content, onComplete }: ContentRendererProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get signed URL for storage content
  useEffect(() => {
    if (content.content_type === 'image' || content.content_type === 'video') {
      if (content.storage_path) {
        getSignedUrl(content.storage_path);
      } else if (content.external_url) {
        setImageUrl(content.external_url);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [content]);

  // Set up duration timer
  useEffect(() => {
    if (!onComplete) return;

    const timer = setTimeout(() => {
      onComplete();
    }, content.duration_seconds * 1000);

    return () => clearTimeout(timer);
  }, [content, onComplete]);

  const getSignedUrl = async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('signage-content')
        .createSignedUrl(path, 3600); // 1 hour

      if (error) throw error;
      setImageUrl(data.signedUrl);
    } catch (err) {
      console.error('Failed to get signed URL:', err);
      setError('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full bg-slate-900 flex items-center justify-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  switch (content.content_type) {
    case 'image':
      return (
        <div className="w-full h-full bg-black flex items-center justify-center">
          <img
            src={imageUrl || content.external_url}
            alt=""
            className="max-w-full max-h-full object-contain"
            onError={() => setError('Failed to load image')}
          />
        </div>
      );

    case 'video':
      return (
        <div className="w-full h-full bg-black">
          <video
            src={imageUrl || content.external_url}
            className="w-full h-full object-contain"
            autoPlay
            muted
            loop={!onComplete}
            onEnded={onComplete}
            onError={() => setError('Failed to load video')}
          />
        </div>
      );

    case 'html':
      return (
        <div
          className="w-full h-full bg-white overflow-hidden"
          dangerouslySetInnerHTML={{ __html: content.html_content || '' }}
        />
      );

    case 'url':
      return (
        <iframe
          src={content.external_url}
          className="w-full h-full border-0"
          title="External content"
          sandbox="allow-scripts allow-same-origin"
        />
      );

    case 'dashboard':
      return (
        <DashboardRenderer
          dashboardType={content.dashboard_type || 'welcome'}
          dashboardConfig={(content.dashboard_config as Record<string, unknown>) || {}}
          businessId="" // Will be passed from session context
        />
      );

    default:
      return (
        <div className="w-full h-full bg-slate-900 flex items-center justify-center">
          <p className="text-slate-400">Unsupported content type</p>
        </div>
      );
  }
}
